import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { LanguageLoader } from "../assets/languages";
import { SurveyResponseQuestionsAPI, SurveyResponseSaveAnswersAPI } from "../services";
import { PanelSkeleton } from "../components/layouts/Skeleton";
import FieldContainer from "../components/features/FieldContainer";
import Dropdown from "../components/features/Dropdown";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { ConvertNumbers, Toastify } from "../components/utils";

const SurveyResponseQuestions = () =>
{
	const { id } = useParams();
	const navigate = useNavigate();
	const preferences = useSelector((state) => state.preferences);
	const language = LanguageLoader(preferences.language);
	const [loading, setLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [questions, setQuestions] = useState([]);
	const [sections, setSections] = useState([]);
	const [answers, setAnswers] = useState({});

	useEffect(() =>
	{
		FetchQuestions();
	// eslint-disable-next-line
	}, [id]);

	const FetchQuestions = async () =>
	{
		setLoading(true);
		try
		{
			const response = await SurveyResponseQuestionsAPI({ id });
			const responseQuestions = response && Array.isArray(response.questions) ? response.questions : [];
			const responseSections = response && Array.isArray(response.sections) ? response.sections : [];
			const initialAnswers = {};
			responseQuestions.forEach((question) =>
			{
				initialAnswers[question.index] = NormalizeAnswer(question);
			});
			setQuestions(responseQuestions);
			setSections(responseSections);
			setAnswers(initialAnswers);
		}
		catch(ex)
		{
			setQuestions([]);
			setSections([]);
			setAnswers({});
		}
		finally
		{
			setLoading(false);
		}
	}

	const NormalizeAnswer = (question) =>
	{
		if(question.type === 'multiselect')
		{
			return Array.isArray(question.answer) ? question.answer : (question.answer ? [question.answer] : []);
		}
		if(question.type === 'checkbox')
		{
			return question.answer === 'on' || question.answer === '1' || question.answer === true;
		}
		return question.answer ?? '';
	}

	const UpdateAnswer = (index, value) =>
	{
		setAnswers((prev) => ({ ...prev, [index]: value }));
	}

	const RenderStars = (question, max) =>
	{
		const selected = Number(answers[question.index]) || 0;
		return <div className="flex flex-wrap items-center justify-end" style={{direction:'ltr'}}>
			{Array.from({length: max}, (_, starIndex) => starIndex + 1).map((rate) => <button
				key={rate}
				type="button"
				aria-label={String(rate)}
				onClick={() => UpdateAnswer(question.index, String(rate))}
				style={{background:'transparent', border:0, padding:'0 3px', cursor:'pointer'}}
			>
				<i className="fa fa-star" style={{fontSize:22, color: selected >= rate ? '#f5b301' : '#c5c5c5'}} />
			</button>)}
		</div>;
	}

	const RenderQuestionInput = (question) =>
	{
		const value = answers[question.index];
		const options = Array.isArray(question.options) ? question.options : [];
		const dropdownOptions = BuildDropdownOptions(options, true);
		const multiOptions = BuildDropdownOptions(options);
		const dateValue = typeof value === 'string' ? value.replaceAll('-', '/') : value;
		switch(question.type)
		{
			case 'textarea':
			case 'matrix':
				return <textarea className="!transition-none !delay-0" value={value || ''} onChange={(e) => UpdateAnswer(question.index, e.target.value)} rows={2} />;
			case 'checkbox':
				return <div className="form-check form-switch">
					<input type="checkbox" className="form-check-input mt-3" checked={!!value} onChange={(e) => UpdateAnswer(question.index, e.target.checked)} />
				</div>;
			case 'radio':
				return <div className="flex flex-wrap">
					{options.map((option, idx) => <label key={idx} className="mx-3">
						<input
							type="radio"
							name={`question_${question.index}`}
							value={option}
							checked={value === option}
							onChange={(e) => UpdateAnswer(question.index, e.target.value)}
						/>
						<span className="mx-1">{option}</span>
					</label>)}
				</div>;
			case 'dropdown':
			case 'Pointed':
				return <Dropdown
					defaultValue={value}
					options={dropdownOptions}
					onChange={(dropdownValue) => UpdateAnswer(question.index, dropdownValue || '')}
				/>;
			case 'multiselect':
				return <Dropdown
					defaultValue={Array.isArray(value) ? value : []}
					multiple
					options={multiOptions}
					onChange={(dropdownValue) => UpdateAnswer(question.index, dropdownValue || [])}
				/>;
			case 'date':
				return <DatePicker onChange={(e) => UpdateAnswer(question.index, e ? ConvertNumbers(e.format()).replaceAll('/', '-') : '')}
					arrow={false}
					hideOnScroll
					value={dateValue || ''}
					inputClass="date-picker"
					containerClassName="w-full"
					calendar={preferences.language === 'en_US' ? null : persian}
					locale={preferences.language === 'en_US' ? null : persian_fa}
					calendarPosition="bottom-right"
					format='YYYY/MM/DD'
				/>;
			case 'datetime':
				return <DatePicker onChange={(e) => UpdateAnswer(question.index, e ? ConvertNumbers(e.format()).replaceAll('/', '-') : '')}
					arrow={false}
					hideOnScroll
					value={dateValue || ''}
					inputClass="date-picker"
					containerClassName="w-full"
					calendar={preferences.language === 'en_US' ? null : persian}
					locale={preferences.language === 'en_US' ? null : persian_fa}
					calendarPosition="bottom-right"
					format='YYYY/MM/DD HH:mm'
					plugins={[<TimePicker position="bottom" />]}
				/>;
			case 'rating':
				return <div className="flex flex-wrap">
					{[1, 2, 3, 4, 5].map((rate) => <button
						key={rate}
						type="button"
						className={`btn ${Number(value) >= rate ? 'btn-primary' : 'btn-secondary'} mx-1`}
						onClick={() => UpdateAnswer(question.index, String(rate))}
					>{rate}</button>)}
				</div>;
			case 'NPS':
				return RenderStars(question, 10);
			case 'CES':
				return RenderStars(question, 7);
			case 'CSAT':
				return RenderStars(question, 5);
			default:
				return <input type="text" className="form-control" value={value || ''} onChange={(e) => UpdateAnswer(question.index, e.target.value)} />;
		}
	}

	const BuildDropdownOptions = (options, withBlank = false) =>
	{
		const response = {};
		if(withBlank)
		{
			response['0'] = '';
		}
		options.forEach((option) =>
		{
			response[option] = option;
		});
		return response;
	}

	const RenderQuestionFields = (items) =>
	{
		return (items || []).map((question) => <FieldContainer
			key={question.id || question.index}
			className='w-full'
			label={`${question.index}. ${question.question}`}
		>
			{question.description ? <div className="mt-1" style={{fontSize:13, opacity:0.8}}>{question.description}</div> : null}
			<div className="value mt-2">
				{RenderQuestionInput(question)}
			</div>
		</FieldContainer>);
	}

	const RenderSections = () =>
	{
		return sections.map((section, sectionIndex) =>
		{
			if(section.type === 'flat')
			{
				return <div key={`flat-${sectionIndex}`} className="flex flex-wrap w-full">
					{RenderQuestionFields(section.questions)}
				</div>;
			}
			const subcategories = Array.isArray(section.subcategories) ? section.subcategories : [];
			return <div key={section.id || sectionIndex} className="survey-category">
				<div className="survey-category-title">
					{section.name}
				</div>
				{subcategories.length ? subcategories.map((subcategory) => <div key={subcategory.id} className="survey-subcategory">
					<div className="survey-subcategory-title">
						{subcategory.name}
					</div>
					<div className="flex flex-wrap">
						{RenderQuestionFields(subcategory.questions)}
					</div>
				</div>) : <div className="flex flex-wrap">
					{RenderQuestionFields(section.questions)}
				</div>}
			</div>;
		});
	}

	const SaveAnswers = async () =>
	{
		if(isSaving || !id)
		{
			return;
		}
		setIsSaving(true);
		try
		{
			const response = await SurveyResponseSaveAnswersAPI({ id, answers });
			if(response && response.saved)
			{
				if(response.thanks_text && response.thanks_text.length)
				{
					Toastify(response.thanks_text);
				}
				navigate('/detail/Surveys_Response/'+id);
			}
		}
		catch(ex){}
		finally
		{
			setIsSaving(false);
		}
	}

	const hasQuestions = questions.length > 0;

	return (
		<>
		{loading ? <PanelSkeleton/> :
		<div className='wrapper'>
			<div className='title flex justify-between w-full'>
				<h6>{language.SURVEY_RESPONSE_QUESTIONS}</h6>
				<div className="flex">
					<button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>{language.AUTHENTICATION.RETURN}</button>
					<button type="button" className="btn-primary" onClick={SaveAnswers} disabled={isSaving}>{isSaving ? <i className="fa fa-spin fa-spinner" style={{padding: '0 7.3px'}}></i> : language.ANSWER}</button>
				</div>
			</div>
			<div className="flex flex-wrap">
				{hasQuestions ? (sections.length ? RenderSections() : <div className="flex flex-wrap w-full">{RenderQuestionFields(questions)}</div>) : <div>{language.NO_DATA}</div>}
			</div>
		</div>
		}
		</>
	);
}

export default SurveyResponseQuestions;
